// Single source of truth for exercise-builder block types, mirroring the
// GAME_TYPES convention in gameTypes.js - every block-chrome/type-picker/
// editor file reads from here instead of hardcoding type strings. Static
// blocks map to ExerciseTemplateElement.type, interactive ones map to
// ExerciseTemplateInteractiveElement.type + a frontend-owned `config` JSON
// shape (the backend validates nothing in config - the shape below is
// this build's own contract, documented per-type here).

export const BLOCK_KIND_STATIC = "static";
export const BLOCK_KIND_INTERACTIVE = "interactive";

const STATIC_BLOCK_TYPES = {
    heading: {
        type: "heading", kind: BLOCK_KIND_STATIC, label: "Заголовок", accent: "#63dac8",
        defaultConfig: {},
    },
    text: {
        type: "text", kind: BLOCK_KIND_STATIC, label: "Текст", accent: "#7ec2e8",
        // text itself carries the **bold** subset directly, config unused
        defaultConfig: {},
    },
    list: {
        type: "list", kind: BLOCK_KIND_STATIC, label: "Список", accent: "#e0b04c",
        // config: {style:'bullet'|'number', items:string[]}
        defaultConfig: { style: "bullet", items: [""] },
    },
    image: {
        type: "image", kind: BLOCK_KIND_STATIC, label: "Зображення", accent: "#c77dff",
        // config: {url, alt, caption}
        defaultConfig: { url: "", alt: "", caption: "" },
    },
    audio: {
        type: "audio", kind: BLOCK_KIND_STATIC, label: "Аудіо", accent: "#f0954c",
        // config: {url, caption}
        defaultConfig: { url: "", caption: "" },
    },
    video: {
        type: "video", kind: BLOCK_KIND_STATIC, label: "Відео", accent: "#e0876c",
        // config: {url, caption}
        defaultConfig: { url: "", caption: "" },
    },
    video_embed: {
        type: "video_embed", kind: BLOCK_KIND_STATIC, label: "Відео за посиланням", accent: "#a2a2a2",
        // config: {url, description} - a pasted YouTube/Vimeo link, embedded
        // via VideoEmbedPreview.jsx - deliberately separate from `video`'s
        // self-hosted-file playback (raw <video src>, can't play a page
        // link). Accent matches the embed_* family's "this is an embed" grey.
        defaultConfig: { url: "", description: "" },
    },
};

const INTERACTIVE_BLOCK_TYPES = {
    multiple_choice: {
        type: "multiple_choice", kind: BLOCK_KIND_INTERACTIVE, label: "Вибір відповіді", accent: "#3ddf93",
        // config: {question, options:[{id,text}], correctIds:[id], multiple:bool}
        defaultConfig: { question: "", options: [{ id: 1, text: "" }, { id: 2, text: "" }], correctIds: [], multiple: false },
    },
    true_false: {
        type: "true_false", kind: BLOCK_KIND_INTERACTIVE, label: "Правда чи ні", accent: "#3ddf93",
        // config: {statement, correct:bool}
        defaultConfig: { statement: "", correct: true },
    },
    ordering: {
        type: "ordering", kind: BLOCK_KIND_INTERACTIVE, label: "Розташувати по порядку", accent: "#3ddf93",
        // config: {prompt, items:string[]} - correct order = array order
        defaultConfig: { prompt: "", items: ["", ""] },
    },
    fill_blank: {
        type: "fill_blank", kind: BLOCK_KIND_INTERACTIVE, label: "Заповнити пропуски", accent: "#3ddf93",
        // config: {template:"I ___ to school", blanks:[{accepted:string[]}]} - split on literal "___"
        defaultConfig: { template: "", blanks: [] },
    },
    matching: {
        type: "matching", kind: BLOCK_KIND_INTERACTIVE, label: "Підбір пар", accent: "#3ddf93",
        // config: {pairs:[{left:{kind,value}, right:{kind,value}}], mediaWidth, mediaHeight}
        // - kind is 'text'|'image'|'audio'|'video' per side (any
        // combination), value is text content or a media URL.
        // mediaWidth/mediaHeight are CSS length strings sizing image/video
        // chips in the preview (audio only uses mediaWidth) - see
        // exercise-templates/matchingConfig.js for the full shape,
        // normalization of the older plain-string pairs shape, and shared
        // defaults (kept out of here to avoid a duplicate source of truth).
        defaultConfig: { pairs: [{ left: { kind: "text", value: "" }, right: { kind: "text", value: "" } }], mediaWidth: "140px", mediaHeight: "100px" },
    },
    essay: {
        type: "essay", kind: BLOCK_KIND_INTERACTIVE, label: "Текстова відповідь", accent: "#3ddf93",
        // config: {prompt} - the task description shown to the student above
        // a large free-text answer area. No "correct" concept (open-ended,
        // not machine-graded) - unlike the other interactive types, this one
        // is reviewed by a teacher reading the actual answer, not scored.
        // Student's answer itself is saved via the existing generic
        // ExerciseStudentSavedInteractiveElement.resultJson mechanism,
        // shape {text:"..."} - same storage every other interactive type
        // already uses, no backend change needed for this type either.
        defaultConfig: { prompt: "" },
    },
    sentence_order: {
        type: "sentence_order", kind: BLOCK_KIND_INTERACTIVE, label: "Скласти речення зі слів", accent: "#3ddf93",
        // config: {prompt, words:string[]} - correct order = array order
        // (same "array order is the answer key" convention as `ordering`,
        // just genuinely interactive here - student taps words in a
        // shuffled bank to build the sentence, matches `matching`'s
        // tap-not-drag interaction convention). Student's built order saved
        // as resultJson {words:string[]} - graded position-by-position
        // against config.words.
        defaultConfig: { prompt: "", words: [] },
    },
    error_spotting: {
        type: "error_spotting", kind: BLOCK_KIND_INTERACTIVE, label: "Знайти помилку в реченні", accent: "#3ddf93",
        // config: {words:string[], errorWordIndex:number} - the sentence
        // pre-split into words, one index marked as the error. Student taps
        // the word they think is wrong; resultJson {pickedWordIndex:number}.
        defaultConfig: { words: [], errorWordIndex: 0 },
    },
    audio_response: {
        type: "audio_response", kind: BLOCK_KIND_INTERACTIVE, label: "Аудіо-відповідь", accent: "#3ddf93",
        // config: {prompt} - identical shape to `essay`, reuses the exact
        // same editor (EssayEditor/renderEssayPreview registered under both
        // keys - same "one editor, several type keys" convention already
        // used for heading/text and image/audio/video). Only the STUDENT-
        // facing answer mechanism differs (records/uploads audio instead of
        // typing) - that lives entirely on the platform side. resultJson
        // {fileId:number} - a bare id into the existing admin `File`
        // entity/MediaStorageService, so CRM's workspace can play it back
        // directly via the already-existing GET /api/files/{id}/content
        // (same admin session/cookie, no new endpoint needed on the CRM
        // read side).
        defaultConfig: { prompt: "" },
    },
    embed_conversation: {
        type: "embed_conversation", kind: BLOCK_KIND_INTERACTIVE, label: "Вбудований тест", accent: "#a2a2a2",
        // uses the `conversation` relation directly, config unused
        defaultConfig: {},
    },
    embed_grid: {
        type: "embed_grid", kind: BLOCK_KIND_INTERACTIVE, label: "Вбудована гра", accent: "#a2a2a2",
        // uses the `grid` relation directly, config unused
        defaultConfig: {},
    },
};

export const EXERCISE_BLOCK_TYPES = { ...STATIC_BLOCK_TYPES, ...INTERACTIVE_BLOCK_TYPES };
export const STATIC_BLOCK_TYPE_LIST = Object.values(STATIC_BLOCK_TYPES);
export const INTERACTIVE_BLOCK_TYPE_LIST = Object.values(INTERACTIVE_BLOCK_TYPES);

export function getBlockType(type) {
    return EXERCISE_BLOCK_TYPES[type] || null;
}
